/**
 * Product-card video slides.
 *
 * Athos feeds carry stills only: a product video surfaces in `ss_images`
 * solely as its auto-generated poster frame (`/files/preview_images/…`).
 * Client-rendered cards keep those posters as ordinary image slides;
 * this module resolves each one's hosted video through the Storefront
 * API — one batched GraphQL request per result set — and upgrades the
 * slide in place:
 *
 *   - posters are matched to their product's videos by preview-image
 *     filename, so multi-video products upgrade every slide correctly;
 *   - the `<video>` is layered over the poster and faded in only once
 *     playback starts (CSS keyed on `data-playing` in
 *     `snippets/product-card.liquid`); the poster stays underneath as
 *     the permanent fallback for stalled loads, blocked autoplay, and
 *     fetch failures;
 *   - lazy-loading follows the slider's contract: in a not-yet-hydrated
 *     slide the video waits on `data-src` for `media-slider.js` to
 *     activate on visibility, in a hydrated one it gets `src` directly.
 *
 * Videos are cached per handle for the page lifetime, so pagination and
 * card swaps only query handles not yet seen. A MutationObserver picks
 * up dynamically injected cards (grid renders, colour swaps). Under
 * `prefers-reduced-motion` no upgrade runs — the still poster is the
 * reduced-motion rendering of a card video. Fetch failures fail open
 * (posters remain) and the module disables itself after repeated
 * failures.
 *
 * While the desktop hover preview (`.product-card__hover-image`, see
 * `snippets/product-card.liquid`) covers a card's media, its playing
 * videos pause and resume from the same frame once the pointer leaves —
 * for any card video, module-upgraded or template-stamped.
 */
(function () {
  'use strict';

  if (window.ProductCardVideo) return;

  const CARD_SELECTOR = '[data-js="product-card"]';
  const SLIDE_SELECTOR = '.media-slider__slide';
  const TRIGGER_SELECTOR = `${CARD_SELECTOR}, ${SLIDE_SELECTOR}`;
  const PENDING_SELECTOR = [
    `${CARD_SELECTOR} img[src*="/preview_images/"]:not([data-video-checked])`,
    `${CARD_SELECTOR} img[data-src*="/preview_images/"]:not([data-video-checked])`,
  ].join(', ');
  const ENDPOINT = ((window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/') + 'api/2026-04/graphql.json';
  const BATCH_SIZE = 40;
  const MAX_FAILURES = 3;

  /**
   * Hosted videos per product handle; `null` marks a handle Shopify does
   * not recognise or a product with no hosted video, so it is never
   * refetched.
   * @type {Map<string, Array<{ stem: string, url: string }>|null>}
   */
  const videoCache = new Map();

  /** @type {Set<string>} Handles with a request currently in flight. */
  const pending = new Set();

  let failureCount = 0;

  /**
   * Read the Storefront API token injected by `layout/theme.liquid`.
   * @returns {string} The token, or an empty string when unconfigured.
   */
  function getToken() {
    if (!window.themeSettings || !window.themeSettings.storefrontApiToken) return '';
    return String(window.themeSettings.storefrontApiToken);
  }

  /**
   * Reduce a preview-image URL to its stable filename stem so the same
   * asset matches across CDN variants: Athos may append a size suffix
   * (`…thumbnail.0000000000_medium.jpg`) and both sides carry differing
   * query params.
   * @param {string} url - An image URL containing `/preview_images/`.
   * @returns {string} The filename stem, or an empty string when the URL
   *   is not a preview image.
   */
  function previewStem(url) {
    const marker = '/preview_images/';
    const start = String(url).indexOf(marker);
    if (start === -1) return '';
    let stem = String(url).slice(start + marker.length);
    const query = stem.indexOf('?');
    if (query !== -1) stem = stem.slice(0, query);
    stem = stem.replace(
      /_(?:\d+x\d*(?:_crop_[a-zA-Z]+)?|small|medium|large|grande|original)(?=\.[a-zA-Z0-9]+$)/i,
      '',
    );
    const extension = stem.lastIndexOf('.');
    if (extension !== -1) stem = stem.slice(0, extension);
    return stem;
  }

  /**
   * Choose the highest-resolution MP4 rendition.
   * @param {Array<{ format: string, height: number, url: string }>} sources
   *   Video sources as returned by the Storefront API.
   * @returns {string} The chosen source URL, or an empty string.
   */
  function pickSource(sources) {
    const mp4s = (Array.isArray(sources) ? sources : [])
      .filter((source) => source && source.url && String(source.format).toLowerCase() === 'mp4');
    if (!mp4s.length) return '';
    return mp4s.reduce((best, source) =>
      (source.height || 0) > (best.height || 0) ? source : best,
    ).url;
  }

  /**
   * Query the Storefront API for a batch of handles and cache each
   * handle's hosted videos. A handle that resolves to null is archived,
   * draft, or unpublished on this store.
   * @param {string[]} handles - Product handles, at most BATCH_SIZE.
   * @returns {Promise<void>}
   */
  async function fetchBatch(handles) {
    const fields = handles
      .map(
        (handle, i) =>
          `p${i}: product(handle: ${JSON.stringify(handle)}) { media(first: 25) { nodes {` +
          ' ... on Video { previewImage { url } sources { format height url } } } } }',
      )
      .join(' ');
    const response = await fetch(ENDPOINT, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': getToken(),
      },
      body: JSON.stringify({ query: `{ ${fields} }` }),
    });
    if (response.status === 401 || response.status === 403) {
      // A rejected token cannot recover within the page lifetime —
      // this store's storefront_api_token setting needs its own token.
      failureCount = MAX_FAILURES;
      throw new Error(`Storefront API ${response.status}`);
    }
    if (!response.ok) throw new Error(`Storefront API ${response.status}`);
    const payload = await response.json();
    if (!payload || !payload.data) throw new Error('Storefront API empty response');
    handles.forEach((handle, i) => {
      const node = payload.data[`p${i}`];
      const nodes = (node && node.media && node.media.nodes) || [];
      const videos = nodes
        .filter((media) => media && Array.isArray(media.sources) && media.sources.length)
        .map((media) => ({
          stem: previewStem((media.previewImage && media.previewImage.url) || ''),
          url: pickSource(media.sources),
        }))
        .filter((video) => video.url);
      videoCache.set(handle, videos.length ? videos : null);
    });
  }

  /**
   * Layer a `<video>` over a poster slide. The poster `<img>` keeps the
   * slide's layout box and stays visible until the video reports
   * `playing`, so a stalled load or blocked autoplay simply leaves the
   * still frame in place. The video mirrors the poster's hydration
   * state: `src` when the slider has already activated the slide,
   * `data-src` for `media-slider.js` to swap on visibility.
   * @param {HTMLImageElement} img - The poster image.
   * @param {Array<{ stem: string, url: string }>} videos - The product's
   *   hosted videos.
   * @returns {void}
   */
  function upgrade(img, videos) {
    const slide = img.closest(SLIDE_SELECTOR);
    if (!slide || img.parentElement !== slide || slide.querySelector('video')) return;

    const posterUrl = img.getAttribute('src') || img.getAttribute('data-src') || '';
    const stem = previewStem(posterUrl);
    let match = videos.find((video) => video.stem && video.stem === stem);
    if (!match && videos.length === 1) match = videos[0];
    if (!match) return;

    const video = document.createElement('video');
    video.dataset.js = 'product-card-video';
    video.width = 200;
    video.height = 290;
    // Muted must be set as a property before playback is requested —
    // several engines ignore the attribute alone on scripted elements
    // and then refuse to autoplay.
    video.muted = true;
    video.setAttribute('muted', '');
    video.loop = true;
    video.setAttribute('loop', '');
    video.autoplay = true;
    video.setAttribute('autoplay', '');
    video.playsInline = true;
    video.setAttribute('playsinline', '');
    video.addEventListener(
      'playing',
      () => {
        video.dataset.playing = 'true';
      },
      { once: true },
    );
    if (img.getAttribute('src')) {
      video.src = match.url;
    } else {
      video.dataset.src = match.url;
    }
    img.after(video);
  }

  /**
   * Apply a cached lookup to one poster and stamp it checked.
   * @param {HTMLImageElement} img - The poster image.
   * @param {Array<{ stem: string, url: string }>|null} videos - Cached
   *   videos for the poster's product.
   * @returns {void}
   */
  function applyCached(img, videos) {
    img.dataset.videoChecked = 'true';
    if (videos && videos.length) upgrade(img, videos);
  }

  /**
   * Upgrade every unchecked poster on the page: apply cached lookups
   * immediately, then batch-fetch unknown handles and re-run once the
   * cache is populated. Failed batches leave their posters unchecked so
   * a later scan can retry, up to MAX_FAILURES.
   * @returns {Promise<void>}
   */
  async function scan() {
    if (failureCount >= MAX_FAILURES || !getToken()) return;
    if (window.themeUtils && window.themeUtils.prefersReducedMotion) return;

    const posters = document.querySelectorAll(PENDING_SELECTOR);
    if (!posters.length) return;

    const unknown = new Set();
    posters.forEach((img) => {
      const card = img.closest(CARD_SELECTOR);
      const handle = card ? card.dataset.productHandle : '';
      if (!handle) {
        img.dataset.videoChecked = 'true';
        return;
      }
      if (videoCache.has(handle)) {
        applyCached(img, videoCache.get(handle));
      } else if (!pending.has(handle)) {
        unknown.add(handle);
      }
    });
    if (!unknown.size) return;

    const queue = Array.from(unknown);
    queue.forEach((handle) => pending.add(handle));

    const batches = [];
    for (let i = 0; i < queue.length; i += BATCH_SIZE) {
      batches.push(queue.slice(i, i + BATCH_SIZE));
    }

    try {
      await Promise.all(batches.map(fetchBatch));
      failureCount = 0;
    } catch (error) {
      failureCount += 1;
    } finally {
      queue.forEach((handle) => pending.delete(handle));
    }

    if (videoCache.size) scan();
  }

  const HOVER_MEDIA_QUERY = window.matchMedia('(hover: hover) and (pointer: fine)');

  /**
   * Pause a card's playing videos while the desktop hover preview covers
   * them, resuming from the same frame once it clears. Pausing is gated on
   * the preview actually revealing (hover-capable pointer, a hover image
   * present, slider at origin, not suppressed — the CSS contract in
   * `snippets/product-card.liquid`); resuming clears every video this
   * module paused regardless of that state, so slide navigation mid-hover
   * cannot strand a frozen frame.
   * @param {MouseEvent} event - A delegated mouseover/mouseout event.
   * @param {boolean} entering - True for mouseover, false for mouseout.
   * @returns {void}
   */
  function handleHoverPreview(event, entering) {
    if (!HOVER_MEDIA_QUERY.matches) return;
    if (!(event.target instanceof Element)) return;
    const media = event.target.closest('.product-card__media');
    if (!media) return;
    const related = event.relatedTarget;
    if (related instanceof Element && media.contains(related)) return;

    if (entering) {
      if (!media.querySelector('.product-card__hover-image')) return;
      if (media.dataset.atOrigin !== 'true' || media.hasAttribute('data-hover-suppressed')) return;
      media.querySelectorAll('.media-slider__slide video').forEach((video) => {
        if (video.paused) return;
        video.pause();
        video.dataset.hoverPaused = 'true';
      });
      return;
    }

    media.querySelectorAll('video[data-hover-paused]').forEach((video) => {
      delete video.dataset.hoverPaused;
      const resumed = video.play();
      if (resumed && typeof resumed.catch === 'function') resumed.catch(() => {});
    });
  }

  document.addEventListener('mouseover', (event) => handleHoverPreview(event, true));
  document.addEventListener('mouseout', (event) => handleHoverPreview(event, false));

  const scheduleScan =
    window.themeUtils && typeof window.themeUtils.debounce === 'function'
      ? window.themeUtils.debounce(scan, 150)
      : scan;

  const observer = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      for (const node of mutation.addedNodes) {
        if (node.nodeType !== Node.ELEMENT_NODE) continue;
        if (node.matches(TRIGGER_SELECTOR) || node.querySelector(TRIGGER_SELECTOR)) {
          scheduleScan();
          return;
        }
      }
    }
  });
  observer.observe(document.body, { childList: true, subtree: true });

  scan();

  window.ProductCardVideo = Object.freeze({ refresh: scan });
})();
